import { Button } from "@/components/elements/Button";
import { button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { FC, useCallback } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

type TImportFileDropZoneProps = {
  disabled?: boolean;
  onSelectFile: (file: File) => void;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ACCEPT = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
};

export const ImportFileDropZone: FC<TImportFileDropZoneProps> = ({ disabled, onSelectFile }) => {
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const tooLarge = fileRejections.some((rejection) => rejection.errors.some((error) => error.code === "file-too-large"));
        toast.error(tooLarge ? "容量は最大10MBまでとなります。" : "アップロード可能なファイル形式はcsv、xlsx、xlsmのみです。");
        return;
      }
      const selected = acceptedFiles[0];
      if (selected) onSelectFile(selected);
    },
    [onSelectFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ACCEPT, maxSize: MAX_FILE_SIZE, multiple: false, disabled });

  return (
    <Box {...getRootProps()}>
      <input {...getInputProps()} />
      <DropZoneArea dragged={isDragActive}>
        <Typography variant="body14">アップロードするファイルをドラッグ&ドロップ</Typography>
        <Typography variant="body14" color={text.secondary}>
          （ファイル形式：csv、xlsx、xlsmに対応）
        </Typography>
        <Typography variant="body14">または</Typography>
        <Button variant="outline" disabled={disabled}>
          ファイルを選択する
        </Button>
        <Stack component="ul" gap="4px" sx={{ pl: "20px", m: 0 }}>
          <Typography component="li" variant="body14" color={text.secondary}>
            データ内に改行は含めないでください。
          </Typography>
          <Typography component="li" variant="body14" color={text.secondary}>
            容量は最大10MBまでとなります。
          </Typography>
        </Stack>
      </DropZoneArea>
    </Box>
  );
};

const styleOptions = { shouldForwardProp: (propName: string) => propName !== "dragged" };

const DropZoneArea = styled(Stack, styleOptions)<{ dragged: boolean }>`
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  border: 1px dashed ${divider.middle};
  border-radius: ${rounded.sm};
  ${({ dragged }) =>
    dragged &&
    css`
      border-color: ${button.primary};
    `}
`;
